import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BlockService } from '../services/block-service.js';
import { ProfileService, DEFAULT_DISPLAY_NAME } from '../services/profile-service.js';
import { useLocale } from '../hooks/useLocale.jsx';

// "차단된 사용자" 관리 페이지 — Account 메뉴에서 진입. 차단된 user 의
// handle/이름 listing + 각 row 별 Unblock 버튼. Apple Guideline 1.2 의
// "차단 해제 가능해야 함" 요구사항 충족.
export function BlockedUsers({ user, onSignInRequest }) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Unblock 진행 중인 row 한정 spinner. uid → true.
  const [unblocking, setUnblocking] = useState({});

  const isLoggedIn = user && !user.isAnonymous;

  const loadPage = useCallback(async (reset = false) => {
    if (!isLoggedIn) { setLoading(false); return; }
    if (reset) setLoading(true); else setLoadingMore(true);
    try {
      const { uids, lastVisible, hasMore: more } = await BlockService.listMyBlocked({
        lastDoc: reset ? null : lastDoc,
      });
      const profMap = await ProfileService.getProfilesByUids(uids);
      // uid 의 profile 이 없을 수도 — 그래도 차단 row 자체는 보여줘야
      // unblock 가능. 그런 경우 minimal placeholder 사용.
      const list = uids.map(u => profMap.get(u) || { uid: u, handle: u.slice(0, 8), displayName: '' });
      setProfiles(prev => reset ? list : [...prev, ...list]);
      setLastDoc(lastVisible);
      setHasMore(more);
    } catch (err) {
      console.warn('load blocked failed:', err);
    } finally {
      if (reset) setLoading(false); else setLoadingMore(false);
    }
  }, [isLoggedIn, lastDoc]);

  useEffect(() => {
    loadPage(true);
  }, [isLoggedIn]); // eslint-disable-line

  const handleUnblock = async (uid) => {
    setUnblocking(prev => ({ ...prev, [uid]: true }));
    try {
      await BlockService.toggleBlock(uid, true);
      // optimistic — list 에서 제거.
      setProfiles(prev => prev.filter(p => p.uid !== uid));
    } catch (err) {
      console.warn('unblock failed:', err);
    } finally {
      setUnblocking(prev => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
    }
  };

  if (!isLoggedIn) {
    return (
      <main className="main">
        <div className="empty-state">
          <i className="material-symbols-rounded empty-icon">block</i>
          <h2>{t('signInToSee')}</h2>
          <button className="btn btn-primary" onClick={() => onSignInRequest?.()}>
            <i className="material-symbols-rounded">login</i>
            {t('signIn')}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="main account-page">
      <h1 style={{ margin: '0 0 1rem 0' }}>{t('blockedUsersTitle')}</h1>
      <p className="account-card-desc" style={{ marginTop: 0 }}>{t('blockedUsersDesc')}</p>

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : profiles.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)' }}>{t('blockedUsersEmpty')}</p>
      ) : (
        <ul className="follow-list" style={{ listStyle: 'none', padding: 0 }}>
          {profiles.map(p => (
            <li key={p.uid} className="follow-list-row" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <button
                type="button"
                onClick={() => navigate(`/u/${p.handle}`)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
              >
                {p.photoURL ? (
                  <img src={p.photoURL} alt="" style={{ width: 44, height: 44, borderRadius: '50%' }} />
                ) : (
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="material-symbols-rounded" style={{ color: 'var(--text-secondary)' }}>person</i>
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>@{p.handle}</div>
                  {p.displayName && p.displayName !== DEFAULT_DISPLAY_NAME && (
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.displayName}</div>
                  )}
                </div>
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => handleUnblock(p.uid)}
                disabled={!!unblocking[p.uid]}
              >
                {unblocking[p.uid] ? t('loading') : t('unblockUser')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {hasMore && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => loadPage(false)}
            disabled={loadingMore}
          >
            {loadingMore ? t('loading') : t('loadMore')}
          </button>
        </div>
      )}
    </main>
  );
}
